if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Buscar cart-notification del header visible
        const visibleHeader = document.querySelector('.header-wrapper.header-main:not([style*="display: none"]), .header-wrapper.header-electronics:not([style*="display: none"])');
        this.cart = (visibleHeader?.querySelector('cart-notification')) || 
                   document.querySelector('cart-notification') || 
                   document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]') || this.querySelector('a[data-bundle-redirect="true"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        if (document.querySelector('cart-drawer') && this.submitButton?.getAttribute?.('type') === 'submit') this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupInventoryByLocationValidation();
      }

      /**
       * Lee las traducciones inyectadas en location-selector-popup.liquid.
       * Si no están, cada caller usa su texto en español como fallback.
       */
      getTranslations() {
        if (this._translations) return this._translations;
        try {
          const script = document.getElementById('location-selector-translations');
          this._translations = script ? JSON.parse(script.textContent) : {};
        } catch (error) {
          this._translations = {};
        }
        return this._translations;
      }

      /**
       * Construye un mapa variantId -> total unidades en carrito (solo productos sueltos + hijos de bundles).
       * No incluye el padre del bundle (suele tener "no seguir inventario"); solo se valida inventario de hijos y sueltos.
       * Normaliza variant_id a número (soporta GID).
       */
      buildVariantQuantitiesFromCart(cart) {
        const map = {};
        const toId = (v) => {
          if (v == null) return null;
          const s = String(v);
          const gidMatch = s.match(/\/ProductVariant\/(\d+)/);
          return gidMatch ? parseInt(gidMatch[1], 10) : parseInt(s, 10);
        };
        (cart.items || []).forEach((item) => {
          const isBundleParent = item.properties && item.properties._bundle_children && item.properties._bundle_children_info;
          if (!isBundleParent) {
            const lineVariantId = toId(item.variant_id);
            if (lineVariantId) map[lineVariantId] = (map[lineVariantId] || 0) + (item.quantity || 1);
          }
          if (isBundleParent) {
            try {
              const children = JSON.parse(item.properties._bundle_children);
              const info = JSON.parse(item.properties._bundle_children_info);
              const qtyMultiplier = item.quantity || 1;
              (Array.isArray(children) ? children : []).forEach((child, index) => {
                const qty = (child.quantity || 1) * qtyMultiplier;
                let variantId = null;
                const inf = Array.isArray(info) ? (info.find((i) => String(i.cartLineId) === String(child.cartLineId)) || info[index]) : null;
                if (inf) variantId = toId(inf.variantId || inf.variant_id);
                if (variantId) map[variantId] = (map[variantId] || 0) + qty;
              });
            } catch (_) {}
          }
        });
        return map;
      }

      /**
       * Valida inventario por sucursal: cart.attributes.pickup_location_id, unidades ya en carrito
       * (incl. hijos de bundles), proxy /apps/inventory. Retorna { allowed, message, availableToAdd }.
       */
      async validateInventoryByLocation(variantId, quantity) {
        try {
          if (!variantId || !routes?.cart_url) return { allowed: true };
          const variantIdNum = parseInt(String(variantId).replace(/.*\/(\d+)$/, '$1'), 10) || parseInt(variantId, 10);
          const cartRes = await fetch(`${routes.cart_url}.js`);
          const cart = await cartRes.json();
          const locationId = (cart.attributes && cart.attributes.pickup_location_id) || '';
          if (!locationId) {
            return { allowed: false, noLocation: true };
          }
          const variantQuantities = this.buildVariantQuantitiesFromCart(cart);
          const inCart = variantQuantities[variantIdNum] || 0;
          let inventoryQuantity = null;
          const proxyUrl = `/apps/inventory?variantIds=${variantIdNum}&locationId=${encodeURIComponent(locationId)}`;
          try {
            const invRes = await fetch(proxyUrl);
            let data = null;
            const text = await invRes.text();
            if (invRes.ok) {
              try {
                data = text ? JSON.parse(text) : null;
              } catch (_) {
                data = null;
              }
            }
            if (invRes.ok && data) {
              const inv = (data.inventory && data.inventory[variantIdNum]) || data.inventory?.[String(variantIdNum)] || data;
              const raw = inv && (inv.inventoryQuantity != null ? inv.inventoryQuantity : inv.quantity);
              if (raw != null && raw !== '') inventoryQuantity = parseInt(raw, 10);
            }
          } catch (_) {}
          if (inventoryQuantity == null) {
            return { allowed: true };
          }
          const availableToAdd = Math.max(0, inventoryQuantity - inCart);
          const t = this.getTranslations();
          if (availableToAdd <= 0) {
            return {
              allowed: false,
              message: inCart > 0
                ? (t.inventoryNoMore || 'No hay más disponibilidad de este producto en tu sucursal ([in_cart] ya están en tu carrito).').replace('[in_cart]', inCart)
                : t.inventoryNone || 'No hay disponibilidad de este producto en tu sucursal seleccionada.',
              availableToAdd: 0,
            };
          }
          if (quantity > availableToAdd) {
            return {
              allowed: false,
              message: (
                t.inventoryLimited ||
                'Solo hay [available] unidades disponibles en tu sucursal ([in_cart] ya están en tu carrito). Puedes agregar hasta [max] más.'
              )
                .replace('[available]', inventoryQuantity)
                .replace('[in_cart]', inCart)
                .replace('[max]', availableToAdd),
              availableToAdd,
            };
          }
          return { allowed: true, availableToAdd };
        } catch (e) {
          console.warn('Inventory by location validation error:', e);
          return { allowed: true };
        }
      }

      setupInventoryByLocationValidation() {
        const runForVariant = (variantId) => {
          if (!variantId || this.submitButton?.dataset?.bundleRedirect === 'true') return;
          this.validateInventoryByLocation(variantId, 1).then((result) => {
            const qtyInput = document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);
            if (qtyInput && result.availableToAdd != null) {
              const max = Math.max(0, result.availableToAdd);
              qtyInput.setAttribute('max', max);
              const val = parseInt(qtyInput.value, 10) || 0;
              if (val > max) qtyInput.value = max;
            }
            if (!result.allowed && this.submitButton && this.submitButton.getAttribute('type') === 'submit') {
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButton.setAttribute('disabled', 'disabled');
              if (result.noLocation) {
                if (this.submitButtonText) this.submitButtonText.textContent = (window.variantStrings && window.variantStrings.addToCart) || 'Agregar al carrito';
                this.showNoLocationNotice();
              } else {
                if (this.submitButtonText) this.submitButtonText.textContent = this.getTranslations().inventoryOutOfStockButton || 'Sin stock en tu sucursal';
                this.hideNoLocationNotice();
              }
            } else if (result.allowed && this.submitButton?.getAttribute?.('type') === 'submit') {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.removeAttribute('disabled');
              if (this.submitButtonText) this.submitButtonText.textContent = (window.variantStrings && window.variantStrings.addToCart) || 'Agregar al carrito';
              this.hideNoLocationNotice();
              if (typeof window.checkBranch === 'function') window.checkBranch(String(variantId));
            }
          });
        };
        const variantInput = this.form.querySelector('input[name="id"]');
        if (variantInput) {
          runForVariant(variantInput.value);
          variantInput.addEventListener('change', () => runForVariant(variantInput.value));
        }
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          if (PUB_SUB_EVENTS.variantChange) {
            subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
              const id = evt?.data?.variant?.id || evt?.data?.variant?.variant_id;
              if (id) setTimeout(() => runForVariant(id), 150);
              const price = evt?.data?.variant?.price;
              if (price != null) this.dataset.dutyFreePrice = price;
            });
          }
          if (PUB_SUB_EVENTS.cartUpdate) {
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
              const variantInput = this.form?.querySelector('input[name="id"]');
              if (variantInput?.value) setTimeout(() => runForVariant(variantInput.value), 100);
            });
          }
        }

        // Cuando el usuario confirma una nueva sucursal en el popup, revalidar inventario
        // para desbloquear el botón si hay stock en la sucursal seleccionada
        window.addEventListener('locationChanged', () => {
          const variantInput = this.form?.querySelector('input[name="id"]');
          if (variantInput?.value) {
            // Pequeño delay para que los cart attributes estén actualizados
            setTimeout(() => runForVariant(variantInput.value), 200);
          }
        });
      }

      showNoLocationNotice() {
        const noticeId = 'cs-no-location-notice';
        if (document.getElementById(noticeId)) return;
        const notice = document.createElement('div');
        notice.id = noticeId;
        notice.setAttribute('role', 'status');
        notice.style.cssText = [
          'display:flex', 'align-items:flex-start', 'gap:0.8rem',
          'padding:1.2rem 1.4rem', 'margin-top:1.2rem',
          'border-left:3px solid rgba(var(--color-foreground),0.4)',
          'background:rgba(var(--color-foreground),0.04)',
          'border-radius:0.4rem',
        ].join(';');
        const t = this.getTranslations();
        notice.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            style="flex:0 0 auto;margin-top:0.1rem;opacity:0.6" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style="flex:1">
            <p style="margin:0 0 0.6rem;font-size:1.4rem;line-height:1.5;color:rgba(var(--color-foreground),0.8)">
              ${t.noLocationNotice || 'Selecciona una sucursal para ver la disponibilidad de este producto.'}
            </p>
            <button type="button"
              style="display:inline-flex;align-items:center;gap:0.4rem;font-size:1.3rem;font-weight:600;
                     color:rgba(var(--color-foreground),0.85);background:none;border:1px solid rgba(var(--color-foreground),0.25);
                     border-radius:10rem;padding:0.4rem 1rem;cursor:pointer;transition:all 0.2s"
              onmouseover="this.style.background='rgba(var(--color-button),1)';this.style.color='rgb(var(--color-button-text))';this.style.borderColor='rgba(var(--color-button),1)'"
              onmouseout="this.style.background='none';this.style.color='rgba(var(--color-foreground),0.85)';this.style.borderColor='rgba(var(--color-foreground),0.25)'"
              onclick="window.locationSelectorPopup?.open()">
              ${t.selectBranchButton || 'Seleccionar sucursal'}
            </button>
          </div>`;
        this.submitButton?.closest('.product-form__buttons, .shopify-payment-button, [data-type]')
          ?.parentElement?.appendChild(notice)
          || this.submitButton?.parentElement?.appendChild(notice);
      }

      hideNoLocationNotice() {
        document.getElementById('cs-no-location-notice')?.remove();
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton?.dataset?.bundleRedirect === 'true') return;
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || 1);
        const dutyFreeCategory = this.dataset.dutyFreeCategory;

        if (dutyFreeCategory) {
          const dutyFreeCheck = await this.validateDutyFreeLimit(dutyFreeCategory, quantity);
          if (dutyFreeCheck && !dutyFreeCheck.allowed) {
            this.handleErrorMessage(dutyFreeCheck.message);
            return;
          }
        }

        const invCheck = await this.validateInventoryByLocation(variantId, quantity);
        if (!invCheck.allowed) {
          this.handleErrorMessage(invCheck.message);
          if (invCheck.message && invCheck.message.includes('seleccionar una sucursal') && window.locationSelectorPopup?.open) {
            setTimeout(() => window.locationSelectorPopup.open(), 200);
          }
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const loadingSpinner = this.submitButton.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              // Verificar si el error es por cantidad máxima
              const errorMessage = response.description || response.message || '';
              const isQuantityError = errorMessage.includes('solo puedes agregar') || 
                                     errorMessage.includes('only add') ||
                                     errorMessage.includes('cantidad') ||
                                     errorMessage.includes('quantity') ||
                                     errorMessage.includes('máximo') ||
                                     errorMessage.includes('maximum');
              
              if (isQuantityError) {
                // Intentar manejar como bundle: agregar más unidades de productos hijos
                const handled = await this.handleBundleQuantityError(variantId, quantity);
                
                if (handled) {
                  // Si se manejó exitosamente, no mostrar error
                  this.error = false;
                  this.submitButton.removeAttribute('aria-disabled');
                  this.submitButton.classList.remove('loading');
                  const loadingSpinner = this.submitButton.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
                  if (loadingSpinner) {
                    loadingSpinner.classList.add('hidden');
                  }
                  if (this.cart && this.cart.classList.contains('is-empty')) {
                    this.cart.classList.remove('is-empty');
                  }
                  return;
                }
              }
              
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const loadingSpinner = this.submitButton.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      productTypeToDutyFreeCategory(productType) {
        const pt = (productType || '').toLowerCase().trim();
        if (!pt) return null;
        if (pt === 'licor' || pt === 'licores' || pt.includes('licor')) return 'licores';
        if (pt.includes('fragancia') || pt.includes('perfumer')) return 'fragancias';
        if (pt === 'cigarros' || pt === 'cigarrillos' || pt.includes('cigarrillo') || pt.includes('cigarro') || pt.includes('tabaco')) return 'cigarrillos';
        if (pt.includes('celular')) return 'celulares';
        if (pt.includes('electronico') || pt.includes('electrónico')) return 'electronicos';
        return null;
      }

      /**
       * Extrae el volumen en ml de un texto de variante (ej: "750 ml", "1000 ml / Rojo").
       * @returns {number} ml, o 0 si no se puede determinar
       */
      parseDutyFreeMl(str) {
        if (!str) return 0;
        const m = String(str).match(/(\d+(?:[.,]\d+)?)\s*ml\b/i);
        if (m) return Math.round(parseFloat(m[1].replace(',', '.'))) || 0;
        return 0;
      }

      /**
       * Tipo de vuelo efectivo para límites de licores:
       * terminal-2 (Tienda de Llegada 2) siempre 'llegada'; en terminal-1 lo
       * elegido por el pasajero; sin selección → 'llegada' (fallback del cliente).
       */
      getDutyFreeFlightType() {
        try {
          const d = JSON.parse(localStorage.getItem('locationData') || 'null');
          if (d) {
            if (d.terminal === 'terminal-2') return 'llegada';
            if (d.flight_type === 'salida' || d.flight_type === 'llegada') return d.flight_type;
          }
        } catch (_) {}
        return 'llegada';
      }

      async validateDutyFreeLimit(category, quantityToAdd) {
        try {
          if (typeof routes === 'undefined' || !routes.cart_url) return { allowed: true };
          let config = { licores: 10, fragancias: 10, cigarrillos: 1, celulares: 3, electronicos: 10 };
          const configEl = document.getElementById('duty-free-limits-config');
          if (configEl) {
            try {
              config = JSON.parse(configEl.textContent);
            } catch (_) {}
          }
          const limit = config[category];
          // 0 (o negativo) = sin límite para esa categoría
          if (limit == null || limit <= 0) return { allowed: true };

          const [cartRes, sectionsRes] = await Promise.all([
            fetch(`${routes.cart_url}.js`),
            fetch(`${routes.cart_url}?sections=cart-bundle-data`)
          ]);
          const cart = await cartRes.json();
          const sectionsData = await sectionsRes.json();
          const sections = sectionsData.sections || {};
          const sectionKey = sections['cart-bundle-data'] ? 'cart-bundle-data' : Object.keys(sections).find(k => k.includes('cart-bundle'));
          const sectionHtml = sectionKey ? sections[sectionKey] : null;
          let bundles = [];
          if (sectionHtml) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const script = doc.querySelector('script[data-bundle-data]');
            if (script) {
              try {
                const data = JSON.parse(script.textContent);
                bundles = data.bundles || [];
              } catch (_) {}
            }
          }

          const counts = { licores: 0, fragancias: 0, cigarrillos: 0, celulares: 0, electronicos: 0 };
          // Licores se mide solo en ml; ítems sin volumen identificable no se validan.
          const licoresVol = { ml: 0 };
          const addLicores = (qty, mlPerUnit) => {
            if (mlPerUnit > 0) licoresVol.ml += mlPerUnit * qty;
          };
          const matchBundle = (b, item) => String(b.parentItemId) === String(item.id) || Number(b.parentItemId) === Number(item.id);

          for (const item of cart.items || []) {
            const bundle = bundles.find(b => matchBundle(b, item));
            if (bundle && bundle.children && bundle.children.length) {
              for (const child of bundle.children) {
                const cat = this.productTypeToDutyFreeCategory(child.productType);
                const qty = (child.quantity || 1) * (item.quantity || 1);
                if (cat) counts[cat] += qty;
                if (cat === 'licores') addLicores(qty, this.parseDutyFreeMl(child.variantTitle || child.title || ''));
              }
            } else if (item.properties && item.properties._bundle_children) {
              try {
                const bundleChildren = JSON.parse(item.properties._bundle_children);
                const childrenInfo = (item.properties._bundle_children_info && JSON.parse(item.properties._bundle_children_info)) || [];
                const qtyMultiplier = item.quantity || 1;
                const childrenArr = Array.isArray(bundleChildren) ? bundleChildren : [];
                childrenArr.forEach((child, index) => {
                  const qty = (child.quantity || 1) * qtyMultiplier;
                  let info = Array.isArray(childrenInfo) ? childrenInfo.find((i) => String(i.cartLineId) === String(child.cartLineId)) : null;
                  if (!info && Array.isArray(childrenInfo) && index < childrenInfo.length) {
                    info = childrenInfo[index];
                  }
                  const productType = info && (info.productType || info.product_type);
                  const cat = this.productTypeToDutyFreeCategory(productType);
                  if (cat) counts[cat] += qty;
                  // Hijos de bundle por properties: ml desde variantTitle del _bundle_children_info
                  if (cat === 'licores') addLicores(qty, this.parseDutyFreeMl(info && (info.variantTitle || info.variant_title)));
                });
              } catch (_) {
                const cat = this.productTypeToDutyFreeCategory(item.product_type);
                const qty = item.quantity || 1;
                if (cat) counts[cat] += qty;
                if (cat === 'licores') addLicores(qty, this.parseDutyFreeMl(item.variant_title || ''));
              }
            } else {
              const cat = this.productTypeToDutyFreeCategory(item.product_type);
              const qty = item.quantity || 1;
              if (cat) counts[cat] += qty;
              if (cat === 'licores') addLicores(qty, this.parseDutyFreeMl(item.variant_title || ''));
            }
          }

          // Licores: límite en litros por tipo de vuelo. Ítems sin volumen
          // identificable no se validan (decisión explícita 2026-08-17: se
          // quitó el límite/conteo por unidades).
          if (category === 'licores' && limit && typeof limit === 'object') {
            const ft = this.getDutyFreeFlightType();
            const lim = limit[ft] || limit.llegada || {};
            const limMl = parseInt(lim.litros_ml, 10) || 0;
            // Solo texto para el mensaje; la lógica usa ft
            const t = this.getTranslations();
            const ftLabel =
              ft === 'salida' ? t.dutyFreeFlightDeparting || 'vuelo de salida' : t.dutyFreeFlightArriving || 'vuelo de llegada';

            let variantMl = 0;
            const mlMapEl = this.querySelector('[data-duty-free-ml-map]');
            if (mlMapEl) {
              try {
                const mlMap = JSON.parse(mlMapEl.textContent);
                const vid = this.form?.querySelector('input[name="id"]')?.value;
                if (vid && mlMap[vid] != null) variantMl = parseInt(mlMap[vid], 10) || 0;
              } catch (_) {}
            }

            const fmtL = (ml) => (ml / 1000).toLocaleString('es', { maximumFractionDigits: 2 });

            if (variantMl > 0 && limMl > 0 && licoresVol.ml + variantMl * quantityToAdd > limMl) {
              const message = (
                t.dutyFreeAddLimitLiters ||
                'Solo puedes llevar hasta [limit] litros de licores en tu [flight]. Ya tienes [current] litros en el carrito.'
              )
                .replace('[limit]', fmtL(limMl))
                .replace('[flight]', ftLabel)
                .replace('[current]', fmtL(licoresVol.ml));
              return { allowed: false, message };
            }
            return { allowed: true };
          }

          const t = this.getTranslations();
          const categoryLabels = t.dutyFreeCategories || {
            licores: 'licores',
            fragancias: 'fragancias',
            cigarrillos: 'cigarrillos',
            celulares: 'celulares',
            electronicos: 'electrónicos',
          };
          const categoryLabel = categoryLabels[category] || category;

          const currentInCategory = counts[category] || 0;
          const maxPermitido = Math.max(0, limit - currentInCategory);
          if (quantityToAdd > maxPermitido) {
            const message = (
              t.dutyFreeAddLimitUnits ||
              'Solo puedes llevar hasta [limit] unidades de [category]. Ya tienes [current] en el carrito. Puedes agregar hasta [max] más.'
            )
              .replace('[limit]', limit)
              .replace('[category]', categoryLabel)
              .replace('[current]', currentInCategory)
              .replace('[max]', maxPermitido);
            return { allowed: false, message };
          }

          const priceLimit = config.priceLimits && config.priceLimits[category];
          if (priceLimit != null && priceLimit > 0) {
            const unitPrice = parseInt(this.dataset.dutyFreePrice || 0);
            let currentSubtotal = 0;
            for (const item of cart.items || []) {
              if (this.productTypeToDutyFreeCategory(item.product_type) === category) {
                currentSubtotal += (item.price || 0) * (item.quantity || 1);
              }
            }
            const newSubtotal = currentSubtotal + unitPrice * quantityToAdd;
            if (newSubtotal > priceLimit) {
              const fmt = (cents) => '$' + (cents / 100).toLocaleString('es', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              const message = (
                t.dutyFreeAddLimitValue || 'Solo puedes llevar hasta [limit] en [category]. Ya tienes [current] en el carrito.'
              )
                .replace('[limit]', fmt(priceLimit))
                .replace('[category]', categoryLabel)
                .replace('[current]', fmt(currentSubtotal));
              return { allowed: false, message };
            }
          }

          return { allowed: true };
        } catch (e) {
          return { allowed: true };
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton || this.submitButton.dataset?.bundleRedirect === 'true') return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      async handleBundleQuantityError(variantId, quantity) {
        // Intentar obtener información del bundle desde el carrito
        // Si el bundle ya está en el carrito, agregar más unidades de los productos hijos
        try {
          // Obtener el carrito actual
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          const cart = await cartResponse.json();
          
          // Buscar si este variant ya está en el carrito como bundle padre
          const existingBundleItem = cart.items.find(item => 
            item.variant_id === parseInt(variantId) && 
            item.quantity >= 1
          );
          
          if (existingBundleItem) {
            // Obtener los datos del bundle desde la sección de Liquid
            const sectionsResponse = await fetch(`${routes.cart_url}?sections=cart-bundle-data`);
            const sectionsData = await sectionsResponse.json();
            
            // Parsear la sección para obtener los productos hijos del bundle
            const bundleDataSection = sectionsData.sections?.['cart-bundle-data'];
            if (bundleDataSection) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(bundleDataSection, 'text/html');
              const bundleDataScript = doc.querySelector('script[data-bundle-data]');
              
              if (bundleDataScript) {
                const bundleData = JSON.parse(bundleDataScript.textContent);
                const bundleInfo = bundleData.bundles?.find(b => 
                  b.parentVariantId === parseInt(variantId) || 
                  b.parentItemId === existingBundleItem.id
                );
                
                if (bundleInfo && bundleInfo.children && bundleInfo.children.length > 0) {
                  // Agregar más unidades de los productos hijos al carrito
                  // El cart transform function de Shopify manejará la creación/actualización del bundle
                  const addPromises = bundleInfo.children.map(child => {
                    const childFormData = new FormData();
                    childFormData.append('id', child.variantId);
                    childFormData.append('quantity', quantity);
                    
                    const config = fetchConfig('javascript');
                    config.headers['X-Requested-With'] = 'XMLHttpRequest';
                    delete config.headers['Content-Type'];
                    config.body = childFormData;
                    
                    return fetch(`${routes.cart_add_url}`, config).then(r => r.json());
                  });
                  
                  const results = await Promise.all(addPromises);
                  
                  // Verificar si todas las adiciones fueron exitosas
                  const allSuccess = results.every(r => !r.status);
                  
                  if (allSuccess) {
                    // Obtener el carrito actualizado con las secciones necesarias
                    if (this.cart) {
                      const sections = this.cart.getSectionsToRender().map((section) => section.id);
                      sections.push('cart-bundle-data');
                      const updatedSectionsResponse = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`);
                      const updatedSectionsData = await updatedSectionsResponse.json();
                      this.cart.renderContents(updatedSectionsData);
                    }
                    
                    return true;
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error al manejar bundle quantity:', error);
        }
        
        return false;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
